import { selectCurrentEntity } from '@opengov/app-core/entity';
import { composeEffects } from '@opengov/app-core/module';
import { ICurrentEntity } from '@opengov/app-core/entity/selectors';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/launchDarkly';
import { getLenses, getStandardReportLens } from '@opengov/api-deprecated';
import { IReportDatasetLens, IReportDatasetLensResponse, ReportTemplateGroup } from '@opengov/reporting-types';

import { selectCurrentReportConfigurationId } from '../common/report/selectors/reportSelectors';
import {
  selectCurrentTemplateStandardLensType,
  selectCurrentTemplateTemplateGroup
} from '../common/reportTemplate/standardTemplateSelectors';
import { selectBudgetFeatureUniqueBaseFlag, selectDatasetLabelsFlag } from '../common/flags';

import { selectLensesWithColumns } from './lensSelectors';
import { lensActionCreators } from './lensState';

export const getLensesWithCursor = async (
  entityUuid: string,
  lensType?: string | string[],
  cursor?: string
): Promise<IReportDatasetLensResponse[]> => {
  //dataset service takes same amount of time to return 1 lens or 500 lenses
  const response = await getLenses({ entityUuid, pageSize: 500, type: lensType, cursor });
  if (response.nextPage.cursor) {
    return response.lenses.concat(await getLensesWithCursor(entityUuid, lensType, response.nextPage.cursor));
  } else {
    return response.lenses;
  }
};

export const lensEffects = composeEffects('lenses', (compose) => ({
  getLensWithColumns: compose.statefulEffect(
    createStructuredSelector({
      lensesWithColumns: selectLensesWithColumns,
      entity: selectCurrentEntity,
      reportConfigurationId: selectCurrentReportConfigurationId
    }),
    async ({ lensesWithColumns, entity, reportConfigurationId }, lensId: string): Promise<IReportDatasetLens> => {
      // Don't fetch something we already have.
      const lens = lensesWithColumns.find((lens) => lens.id === lensId);

      if (lens) {
        return lens;
      }

      lensActionCreators.pushLensWithColumnQueue(lensId);
      const lensWithColumns = await getStandardReportLens(lensId, entity.id, reportConfigurationId);
      if (typeof lensWithColumns === 'string') {
        lensActionCreators.setLensesLoading(false);
        lensActionCreators.popLensWithColumnQueue(lensId);
        throw new Error(lensWithColumns);
      } else {
        lensActionCreators.addLensWithColumns(lensWithColumns);
        lensActionCreators.popLensWithColumnQueue(lensId);
        return lensWithColumns;
      }
    }
  ),
  getEntityLenses: compose.statefulPayloadlessEffect(
    createSelector([selectCurrentEntity, selectFeatureFlags], (...args) => args),
    async ([currentEntity, featureFlags]): Promise<IReportDatasetLensResponse[]> => {
      lensActionCreators.setLensesLoading(true);
      try {
        const lenses = await getLensesWithCursor(currentEntity.uuid as string);
        if (featureFlags.get('product-nextgen-reporting-feature-dataset-labels')) {
          lenses.forEach((lens) => {
            if (Object.hasOwn(lens, 'standardType')) lens.name += ' ⓢ';
          });
        }
        const withData = lenses.filter((lens) => !!lens.dataUpdatedAt);
        lensActionCreators.setLenses(withData);
        return withData;
      } catch (error) {
        lensActionCreators.setLensesLoading(false);
        throw error;
      }
    }
  ),
  getStandardDatasetLenses: compose.statefulPayloadlessEffect(
    createSelector(
      [
        selectCurrentEntity,
        selectCurrentTemplateStandardLensType,
        selectCurrentTemplateTemplateGroup,
        selectDatasetLabelsFlag,
        selectBudgetFeatureUniqueBaseFlag
      ],
      (...args) => args
    ),
    async ([currentEntity, lensType, templateGroup, datasetLabelsFlag, isUniqueBaseEnabled]: [
      ICurrentEntity,
      string,
      ReportTemplateGroup | undefined,
      boolean,
      boolean
    ]): Promise<IReportDatasetLensResponse[]> => {
      lensActionCreators.setLensesLoading(true);
      try {
        let lenses: IReportDatasetLensResponse[] = [];
        if (isUniqueBaseEnabled && templateGroup === ReportTemplateGroup.budgetSummary) {
          lenses = await getLensesWithCursor(currentEntity.uuid as string, [
            'urn:opengov:lenses/standard/budgeting/budget-summary/1',
            'urn:opengov:lenses/standard/budgeting/budget-summary/2',
            'urn:opengov:lenses/standard/budgeting/budget-summary/3'
          ]);
        } else {
          lenses = await getLensesWithCursor(currentEntity.uuid as string, lensType);
        }
        if (datasetLabelsFlag) {
          lenses.forEach((lens) => {
            if (Object.hasOwn(lens, 'standardType')) lens.name += ' ⓢ';
          });
        }
        const withData = lenses.filter((lens) => !!lens.dataUpdatedAt);
        lensActionCreators.setLenses(withData);
        return withData;
      } catch (error) {
        lensActionCreators.setLensesLoading(false);
        throw error;
      }
    }
  )
}));
